import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import moment from 'moment'

import { Div, Span } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { fetchAccountEngagementSummary } from 'api/AccountActivityAPI'
import TimelineByDay from 'components/ActivityTimeline/TimelineByDay'
import { DateRange } from 'components/DateRangePicker'
import { driftLoadingStateBuilder } from 'components/States'
import { CurrentUserContext } from 'context/currentUserContext'
import { EngagementSummary } from 'modules/accountActivity/domain'
import { getCurrentOrg } from 'selectors'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

interface ActivityTimelineProps {
  accountId: string
  accountName?: string
  dateRange?: DateRange
  setDateRange?: (newDate: DateRange | undefined) => void
  useExternalData?: boolean
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  accountId,
  accountName,
  dateRange,
  setDateRange,
  useExternalData = false
}) => {
  const currentUser = useContext(CurrentUserContext)
  const org = useSelector(getCurrentOrg)
  const [engagementSummary, setEngagementSummary] = useState<EngagementSummary>()
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getTime())
  const [activeDayRef, setActiveDayRef] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    setActiveDayRef(null)
    const date = new Date(selectedMonth)
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getTime()
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime()
    fetchAccountEngagementSummary(
      accountId,
      firstDayOfMonth,
      lastDayOfMonth,
      useExternalData
    ).then(data => setEngagementSummary(data))
  }, [accountId, selectedMonth, useExternalData])

  useEffect(() => {
    if (activeDayRef) {
      activeDayRef.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'center'
      })
    }
  }, [activeDayRef])

  const LoadingComponent = driftLoadingStateBuilder('Loading...')

  const trackTimelineMonthChange = () =>
    currentUser?.orgId &&
    segmentTrack(TrackType.TIMELINE_MONTH_CHANGE, { orgId: currentUser.orgId })

  const subMonth = () => {
    trackTimelineMonthChange()
    setSelectedMonth(
      moment(selectedMonth)
        .subtract(1, 'months')
        .valueOf()
    )
  }
  const addMonth = () => {
    trackTimelineMonthChange()
    setSelectedMonth(
      moment(selectedMonth)
        .add(1, 'months')
        .valueOf()
    )
  }

  if (!engagementSummary) {
    return <LoadingComponent />
  }

  return (
    <Div className={styles.container}>
      <Div className={styles.timelineMonthSelector}>
        <Div onClick={subMonth}>
          <Icon name={'arrow-1-left'} width={12} height={12} />
        </Div>
        <Span className={styles.monthName}>{moment(selectedMonth).format('MMMM YYYY')}</Span>
        <Div onClick={addMonth}>
          <Icon name={'arrow-1-right'} width={12} height={12} />
        </Div>
      </Div>
      <Div className={styles.timelineContainer}>
        <Div className={styles.timelineUsThemLabels}>
          <p>{org?.name || 'Us'}</p>
          <p>{accountName || 'Them'}</p>
        </Div>
        {engagementSummary.engagementByDay.map(day => (
          <TimelineByDay
            setActiveDayRef={setActiveDayRef}
            day={day}
            dateRange={dateRange}
            setDateRange={setDateRange}
            key={day.startOfDay}
          />
        ))}
      </Div>
    </Div>
  )
}

export default ActivityTimeline
